import { useRef, useState } from "react";
import { View } from "react-native";

import type { ModalRef } from "$src/core/components/atoms/Modal/types";
import Text from "$src/core/components/atoms/Text/Text";
import TextInput from "$src/core/components/atoms/TextInput";
import ConfirmationModal from "$src/core/components/molecules/ConfirmationModal/ConfirmationModal";
import SubscriptionOfferCard from "$src/core/components/molecules/SubscriptionOfferCard";
import useSubscription from "$src/core/hooks/use-subscription";
import useTranslation from "$src/core/hooks/use-translation";
import type { SubscriptionOfferDto } from "$src/core/models/dto";
import ErrorPage from "$src/core/pages/ErrorPage/ErrorPage";
import LoadingPage from "$src/core/pages/LoadingPage/LoadingPage";
import Alerter from "$src/core/services/alerting";
import Logging from "$src/core/services/logging/logging";

import $ from "./SubscriptionOffersPanel.styles";

const SubscriptionOffersPanel = () => {
  const { t } = useTranslation();
  const {
    status,
    offers,
    subscription,
    activateSubscriptionOffer,
    cancelSubscription,
  } = useSubscription();

  const activationModalRef = useRef<ModalRef | null>(null);
  const cancellationModalRef = useRef<ModalRef | null>(null);

  const [selectedOffer, setSelectedOffer] =
    useState<SubscriptionOfferDto | null>(null);
  const [selectedAction, setSelectedAction] = useState<
    "upgrade" | "downgrade" | null
  >(null);
  const [coupon, setCoupon] = useState("");

  const handleRequestActivation = async (offer: SubscriptionOfferDto) => {
    setSelectedOffer(offer);
    setSelectedAction(null);
    setCoupon("");
    activationModalRef.current?.present();
  };

  const handleRequestUpgrade = async (offer: SubscriptionOfferDto) => {
    setSelectedOffer(offer);
    setSelectedAction("upgrade");
    setCoupon("");
    activationModalRef.current?.present();
  };

  const handleRequestDowngrade = async (offer: SubscriptionOfferDto) => {
    setSelectedOffer(offer);
    setSelectedAction("downgrade");
    setCoupon("");
    activationModalRef.current?.present();
  };

  const handleRequestCancellation = async (offer: SubscriptionOfferDto) => {
    setSelectedOffer(offer);
    setSelectedAction(null);
    setCoupon("");
    cancellationModalRef.current?.present();
  };

  const handleAbortActivation = () => {
    activationModalRef.current?.dismiss();
  };

  const handleAbortCancellation = () => {
    cancellationModalRef.current?.dismiss();
  };

  const handleConfirmActivation = async () => {
    if (!selectedOffer) return;

    try {
      await activateSubscriptionOffer(selectedOffer.id, {
        action: selectedAction ?? undefined,
        coupon: coupon.trim() || undefined,
      });
      setCoupon("");
      activationModalRef.current?.dismiss();
    } catch (error) {
      Alerter.error(t("ERROR/SUBSCRIPTION_OFFERS/ACTIVATING"));
      Logging.error("Failed to activate subscription offer", { error });
    }
  };

  const handleConfirmCancellation = async () => {
    if (!selectedOffer) return;

    try {
      await cancelSubscription(selectedOffer.id);
      handleAbortCancellation();
    } catch (error) {
      Alerter.error(t("ERROR/SUBSCRIPTION_OFFERS/CANCELLING"));
      Logging.error("Failed to cancel subscription", { error });
    }
  };

  if (status === "pending") {
    return <LoadingPage />;
  }

  if (status === "error") {
    return (
      <ErrorPage
        title={t("ERROR/SUBSCRIPTION_OFFERS_PANEL/TITLE")}
        subtitle={t("ERROR/SUBSCRIPTION_OFFERS_PANEL/SUBTITLE")}
      />
    );
  }

  if (!offers?.length) {
    return (
      <Text preset="body" accessibilityRole="text">
        {t("PROFILE/SUBSCRIPTION_OFFERS/SUBTITLE")}
      </Text>
    );
  }

  const activeOffer = offers.find(
    (offer) => offer.id === subscription?.offerId,
  );
  const restOffers = offers.filter((offer) => offer.id !== activeOffer?.id);

  return (
    <>
      <View style={$.offers}>
        {activeOffer && (
          <View style={$.activeOffer}>
            <SubscriptionOfferCard
              key={activeOffer.id}
              offer={activeOffer}
              onRequestCancellation={() =>
                handleRequestCancellation(activeOffer)
              }
              isActive
            />
          </View>
        )}

        {restOffers.map((offer) => {
          const currentOffer = offers.find(
            (_offer) => _offer.id === subscription?.offerId,
          );
          const isUpgrade =
            currentOffer && offer.price.amount > currentOffer.price.amount;
          const isDowngrade =
            currentOffer && offer.price.amount < currentOffer.price.amount;

          return (
            <SubscriptionOfferCard
              key={offer.id}
              offer={offer}
              isUpgrade={isUpgrade}
              isDowngrade={isDowngrade}
              onRequestActivation={() => handleRequestActivation(offer)}
              onRequestUpgrade={() => handleRequestUpgrade(offer)}
              onRequestDowngrade={() => handleRequestDowngrade(offer)}
              onRequestCancellation={() => handleRequestCancellation(offer)}
            />
          );
        })}
      </View>

      <ConfirmationModal
        ref={activationModalRef}
        title={t("SUBSCRIPTION_OFFERS/ACTIVATION/TITLE", {
          title: selectedOffer?.title ?? "",
        })}
        description={t("SUBSCRIPTION_OFFERS/ACTIVATION/DESCRIPTION", {
          title: selectedOffer?.title ?? "",
        })}
        confirmLabel={t("SUBSCRIPTION_OFFERS/ACTIVATION/CONFIRM")}
        onCancel={handleAbortActivation}
        onConfirm={handleConfirmActivation}
      >
        <TextInput
          placeholder={t(
            "SUBSCRIPTION_OFFERS/ACTIVATION/COUPON/INPUT/PLACEHOLDER",
          )}
          value={coupon}
          onChangeText={setCoupon}
          accessibilityRole="text"
          accessibilityLabel={t(
            "SUBSCRIPTION_OFFERS/ACTIVATION/COUPON/INPUT/PLACEHOLDER",
          )}
          style={$.coupon}
        />
      </ConfirmationModal>

      <ConfirmationModal
        ref={cancellationModalRef}
        title={t("SUBSCRIPTION_OFFERS/CANCELLATION/TITLE", {
          title: selectedOffer?.title ?? "",
        })}
        description={t("SUBSCRIPTION_OFFERS/CANCELLATION/DESCRIPTION", {
          title: selectedOffer?.title ?? "",
        })}
        onCancel={handleAbortCancellation}
        onConfirm={handleConfirmCancellation}
      />
    </>
  );
};

export default SubscriptionOffersPanel;
